'use client';

import { useState } from 'react';
import { getErrorMessage } from '@/lib/api-utils';

interface UseDirectMessageMessages {
  validationError: string;
  sent: string; // must contain {name} placeholder
  sendFailed: string;
  forbidden: string;
  recipientNotFound: string;
}

interface UseDirectMessageOptions {
  messages: UseDirectMessageMessages;
  onToast: (message: string, type: 'success' | 'error') => void;
}

export function useDirectMessage({ messages, onToast }: UseDirectMessageOptions) {
  const [recipientId, setRecipientId] = useState<number | null>(null);
  const [recipientName, setRecipientName] = useState('');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [sending, setSending] = useState(false);

  const isOpen = recipientId !== null;

  const open = (id: number, name: string) => {
    setRecipientId(id);
    setRecipientName(name);
  };

  const close = () => {
    setRecipientId(null);
    setRecipientName('');
    setSubject('');
    setBody('');
    setSending(false);
  };

  const send = async () => {
    if (recipientId === null) return;

    if (!subject.trim() || !body.trim()) {
      onToast(messages.validationError, 'error');
      return;
    }

    setSending(true);

    try {
      const res = await fetch('/api/strapi/email/send-direct', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          recipientId,
          subject: subject.trim(),
          message: body.trim(),
        }),
      });

      if (res.ok) {
        onToast(messages.sent.replace('{name}', recipientName), 'success');
        close();
        return;
      }

      const errorMessage = await getErrorMessage(res);
      if (res.status === 400) {
        onToast(errorMessage || messages.validationError, 'error');
      } else if (res.status === 403) {
        onToast(errorMessage || messages.forbidden, 'error');
      } else if (res.status === 404) {
        onToast(errorMessage || messages.recipientNotFound, 'error');
      } else {
        onToast(messages.sendFailed, 'error');
      }
    } catch (error) {
      console.error('Failed to send direct message:', error);
      onToast(messages.sendFailed, 'error');
    } finally {
      setSending(false);
    }
  };

  return {
    isOpen,
    recipientName,
    subject,
    setSubject,
    body,
    setBody,
    sending,
    open,
    close,
    send,
  };
}
